import { useEffect, useState } from "react";
// import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@/components/ui/empty";
import { Spinner } from "@/components/ui/spinner";
import { LocationService } from "@/services/LocationService";
import { motion } from "framer-motion";
import { Frown } from "lucide-react";
import AddNewLocationDialog from "../AddNewLocationDialog/AddNewLocationDialog";
import type { Location } from "@/types/Location";
import { getLocationLabel } from "@/lib/utils";

const LocationsGrid = () => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [isAddNewLocationDialogOpen, setIsAddNewLocationDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const locationService = new LocationService();

  const animation = {
    initial: { opacity: 0, scale: 0.9, skewX: 5 },
    animate: { opacity: 1, scale: 1, skewX: 0 },
  };

  useEffect(() => {
    let cancelled = false;

    setLoading(true);

    const getData = async () => {
      const permissableLocationIds = await locationService.getPermissableLocations();
      if (cancelled) return;
      setLocations(permissableLocationIds);
      setLoading(false);
    };

    getData();

    return () => {
      cancelled = true;
    };
  }, []);

  const renderEmpty = () => {
    return (
      <Empty className="bg-gray-100 border-2 border-gray-100 rounded-lg p-6 border-solid">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Frown />
          </EmptyMedia>
          <EmptyTitle className="tpr-font text-2xl">No locations yet</EmptyTitle>
        </EmptyHeader>
        <EmptyDescription className="max-w-2/3 mx-auto text-center">
          You haven't got any locations assigned to you yet. If you are a new member, please contact Head Office.
        </EmptyDescription>
      </Empty>
    );
  };

  if (loading) {
    return (
      <div className="bg-gray-100 border-2 border-gray-100 rounded-lg p-6 border-solid flex items-center justify-center min-h-64">
        <Spinner className="size-6 mr-2" />
        <span className="text-lg">Loading locations...</span>
      </div>
    );
  }

  if (!locations || locations.length === 0) {
    return renderEmpty();
  }

  return (
    <div className="bg-gray-100 border-2 border-gray-100 rounded-lg p-6 border-solid dark:bg-slate-900 dark:border-gray-900">
      <div className="grid grid-cols-2 md:grid-cols-3 2xl:grid-cols-5 gap-4">
        {[...locations].sort((a, b) => getLocationLabel(a).localeCompare(getLocationLabel(b))).map((location) => (
          <div key={location.slug} className="relative">
            <a href={`/location/${location.slug}?returnUrl=${encodeURIComponent("/dashboard")}`}>
              <motion.div className="rounded-md" initial={animation.initial} animate={animation.animate}>
                <div className="aspect-3/2 overflow-hidden rounded-t-lg bg-gray-100">
                  <img
                    className="h-auto max-w-full rounded-t-lg transform-gpu scale-100 hover:scale-110 transition-transform duration-300"
                    src={location.locationImageUrl}
                    alt=""
                  />
                </div>
              </motion.div>

              <div className="bg-gray-200 dark:bg-slate-800 rounded-b-lg p-2 text-sm md:text-base min-h-[4rem] relative">
                <span aria-label={`Location ${getLocationLabel(location)}`} className="truncate block">
                  {getLocationLabel(location)}
                </span>
              </div>
            </a>
            {/* <div className="absolute -top-2 -right-2">
              {location.notices && location.notices.length > 0 && (
                <Badge
                  className="h-5 min-w-5 hover:w-8 hover:h-8 hover:bg-amber-600 rounded-full px-1 tabular-nums bg-amber-500 text-black flex justify-center items-center"
                  title="Practice Notices"
                >
                  {location.notices.length}
                </Badge>
              )}
            </div> */}
          </div>
        ))}
      </div>
      <div>
        <Button className="mt-4 w-full" variant="outline" onClick={() => setIsAddNewLocationDialogOpen(true)}>
          Need another location?
        </Button>
      </div>

      <AddNewLocationDialog
        isOpen={isAddNewLocationDialogOpen}
        setIsAddNewLocationDialogOpen={setIsAddNewLocationDialogOpen}
      />
    </div>
  );
};

export default LocationsGrid;
